import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button } from '@react-apps/design-system';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';
import type {
  PendingAlertEvent,
  PendingSourceActions,
} from '../../domain/entities/pending-alert.entity';
import { formatDateTime } from '../shared/display';

const PER_PAGE = 5;

function eventJson(event: PendingAlertEvent): string {
  return JSON.stringify(
    {
      id: event.id,
      eventLabel: event.eventLabel,
      actorName: event.actorName,
      reason: event.reason,
      createdAt: event.createdAt,
      payload: event.payload,
    },
    null,
    2,
  );
}

function EventRow({ event }: { event: PendingAlertEvent }) {
  return (
    <li className="border-(--color-border) bg-(--color-bg-secondary) space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-(--color-text-primary) text-sm font-medium">
          {event.eventLabel}
        </span>
        <span className="text-(--color-text-secondary) text-xs">
          {formatDateTime(event.createdAt)}
        </span>
      </div>
      <div className="text-(--color-text-secondary) flex flex-wrap items-center gap-2 text-xs">
        <span>
          • {event.isSystem ? 'Sistema' : (event.actorName ?? 'Admin')}
        </span>
        {event.reason && <span>· {event.reason}</span>}
      </div>
      <details className="group">
        <summary className="text-(--color-accent) cursor-pointer select-none text-xs font-medium">
          Ver payload (JSON)
        </summary>
        <pre className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-secondary) mt-2 max-h-72 overflow-auto rounded-lg border p-3 text-xs leading-relaxed">
          {eventJson(event)}
        </pre>
      </details>
    </li>
  );
}

/**
 * Histórico de auditoria de um item da Central de Pendências — generaliza
 * a timeline de auditoria do Sales para funcionar com qualquer `PendingSource` que
 * declare `actions.fetchHistory`. Mostra `actorName` já resolvido pelo
 * backend em vez do rótulo genérico "Admin"/"Sistema" quando disponível.
 */
export function PendingAlertHistoryModal({
  itemId,
  fetchHistory,
  open,
  onClose,
}: {
  itemId: string;
  fetchHistory: PendingSourceActions['fetchHistory'];
  open: boolean;
  onClose: () => void;
}) {
  const [page, setPage] = usePageQueryParam('pending_alert_history_page');
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['pending-alerts', 'history', itemId, page],
    queryFn: () => fetchHistory(itemId, { page, perPage: PER_PAGE }),
    enabled: open,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const hasNext = page < totalPages;

  const handleClose = () => {
    setPage(1);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Histórico de eventos"
      description="Eventos de auditoria desta pendência, dos mais recentes aos mais antigos."
      size="md"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total} {data.total === 1 ? 'evento' : 'eventos'} · página{' '}
                {page} de {totalPages}
              </>
            ) : null}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || isFetching}
            >
              Anterior
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={!hasNext || isFetching}
            >
              Ver mais
            </Button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <ul className="space-y-3">
          {Array.from({ length: PER_PAGE }).map((_, i) => (
            <li
              key={i}
              className="border-(--color-border) bg-(--color-bg-secondary) h-20 animate-pulse rounded-lg border"
            />
          ))}
        </ul>
      ) : isError ? (
        <p className="text-(--color-text-secondary) py-8 text-center text-sm">
          Não foi possível carregar os eventos.
        </p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-(--color-text-secondary) py-8 text-center text-sm">
          Nenhum evento registrado para esta entidade.
        </p>
      ) : (
        <ul className="space-y-3">
          {data.items.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** Botão auto-contido: abre o histórico da pendência num modal. */
export function PendingAlertHistoryButton({
  itemId,
  fetchHistory,
}: {
  itemId: string;
  fetchHistory: PendingSourceActions['fetchHistory'];
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Histórico
      </Button>
      <PendingAlertHistoryModal
        itemId={itemId}
        fetchHistory={fetchHistory}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
